export type WholesaleMarginalPriceLimits = {
  import_min?: number
  import_max?: number
  export_min?: number
  export_max?: number
}

export const WHOLESALE_MARGINAL_PRICE_LIMIT_KEYS = [
  'import_min',
  'import_max',
  'export_min',
  'export_max',
] as const satisfies ReadonlyArray<keyof WholesaleMarginalPriceLimits>

const SUPPORTED_COUNTRIES = ['AU'] as const
export type WholesaleCountryIso2 = (typeof SUPPORTED_COUNTRIES)[number]

const AU_WHOLESALE_YEARS = [2022, 2023, 2024, 2025] as const

export const WHOLESALE_SPOT_SERIES_CALENDAR = 'calendar'

export const AU_FALLBACK_WHOLESALE_YEAR = 2025

export function isCalendarWholesaleSpotSeries(raw: unknown): boolean {
  if (raw === undefined || raw === null || raw === '') {
    return true
  }
  return String(raw).trim().toLowerCase() === WHOLESALE_SPOT_SERIES_CALENDAR
}

export type WholesaleSpotSeriesChoice = {
  id: string
  name: string
}
const AU_WHOLESALE_SPOT_SERIES_CHOICES: readonly WholesaleSpotSeriesChoice[] = [
  {
    id: WHOLESALE_SPOT_SERIES_CALENDAR,
    name: 'Calendar year',
  },
  {
    id: '2022-2025',
    name: 'Synthetic mean (2022-2025)',
  },
]

export function wholesaleSpotSeriesChoicesForCountry(country: WholesaleCountryIso2): WholesaleSpotSeriesChoice[] {
  if (country === 'AU') {
    return [...AU_WHOLESALE_SPOT_SERIES_CHOICES]
  }
  return [
    {
      id: WHOLESALE_SPOT_SERIES_CALENDAR,
      name: 'Calendar year — one wholesale file per selected price year',
    },
  ]
}

type CountryEditorConfig = {
  label: string
  wholesaleYears: readonly number[]
  wholesaleForm?: {
    wholesaleToggleLabel?: string
    networkTariffGroupIds?: {
      label: string
      helperText: string
    }
  }
}

const WHOLESALE_COUNTRY_CONFIG = {
  AU: {
    label: 'Australia (NEM)',
    wholesaleYears: AU_WHOLESALE_YEARS,
    wholesaleForm: {
      wholesaleToggleLabel: 'Wholesale tariff (NEM spot + network)',
      networkTariffGroupIds: {
        label: 'Network tariff groups (optional)',
        helperText: 'If set, only these network tariffs are considered when matching the project postcode.',
      },
    },
  },
} as const satisfies Record<WholesaleCountryIso2, CountryEditorConfig>

export const WHOLESALE_TOGGLE_LABEL_DEFAULT = 'Wholesale tariff (spot + network)'

export function getWholesaleCountrySpotConfig(country: WholesaleCountryIso2): CountryEditorConfig {
  return WHOLESALE_COUNTRY_CONFIG[country]
}

export const WHOLESALE_COUNTRY_CHOICES: ReadonlyArray<{ id: WholesaleCountryIso2; name: string }> =
  SUPPORTED_COUNTRIES.map((id) => ({
    id,
    name: WHOLESALE_COUNTRY_CONFIG[id].label,
  }))

export function wholesaleYearChoicesForCountry(country: WholesaleCountryIso2): Array<{ id: number; name: string }> {
  return WHOLESALE_COUNTRY_CONFIG[country].wholesaleYears.map((y) => ({ id: y, name: String(y) }))
}

export function isWholesaleCountryIso2(s: string): s is WholesaleCountryIso2 {
  return (SUPPORTED_COUNTRIES as readonly string[]).includes(s)
}

export function normalizeWholesaleCountryIso2(formValue: unknown): WholesaleCountryIso2 {
  if (formValue == null || formValue === '') {
    return 'AU'
  }
  const upper = String(formValue).trim().toUpperCase()
  return isWholesaleCountryIso2(upper) ? upper : 'AU'
}

export function isWholesaleUtilityTariffData(data: unknown): boolean {
  if (data == null) {
    return false
  }
  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (trimmed === '') {
      return false
    }
    try {
      return isWholesaleUtilityTariffData(JSON.parse(trimmed))
    } catch {
      return trimmed.includes('"wholesale":true') || trimmed.includes('"wholesale": true')
    }
  }
  if (typeof data === 'object') {
    const wholesale = (data as { wholesale?: unknown }).wholesale
    return wholesale === true || wholesale === 'true'
  }
  return false
}
